import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { adminUpdate, adminUsersApi, adminExportApi, deleteUser } from '../../lib/adminApi';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { formatBytes } from '../../lib/imageCompression';
import { getActiveMagicLink } from '../../lib/magicLinkHelper';
import { MagicLinkModal } from '../../components/MagicLinkModal';
import { useUserRole } from '../../hooks/useUserRole';
import {
  Mail, Trash2, X, Building2, Globe, Eye, AlertTriangle, Search,
  ExternalLink, HardDrive, ChevronLeft,
  ChevronRight, ChevronsLeft, ChevronsRight, ArrowUpDown, ArrowUp, ArrowDown,
  User as UserIcon, UserPlus, Link2, Crown, ShieldCheck, Headphones,
  Download, UserMinus
} from 'lucide-react';
import { formatDateNL } from '../../lib/billingDates';
import { computeStoplicht, stoplichtBg } from '../../lib/journeyHelpers';
import type { UserJourneyStatus } from '../../types/klanttraject';

interface User {
  id: string;
  email: string;
  first_name: string;
  company_name: string;
  website_url: string | null;
  storage_used_bytes: number;
  widget_enabled: boolean;
  is_admin: boolean;
  is_super_admin: boolean;
  is_support_user: boolean;
  sessions_count?: number;
  user_number: number | null;
  journey_status?: string | null;
  journey_last_activity_at?: string | null;
}

type SortColumn = 'first_name' | 'email' | 'company_name' | 'created_at' | 'storage_used_bytes';
type SortOrder = 'asc' | 'desc';
type UserTypeFilter = 'all' | 'regular' | 'admin' | 'support';

export function AdminUsers() {
  const navigate = useNavigate();
  const { canDelete, role } = useUserRole();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [cancellationsByUser, setCancellationsByUser] = useState<Record<string, { effective_end_date: string }>>({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [userNumberFilter, setUserNumberFilter] = useState('');
  const [sortColumn, setSortColumn] = useState<SortColumn>('created_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [userTypeFilter, setUserTypeFilter] = useState<UserTypeFilter>('regular');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const perPage = 25;

  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUserData, setNewUserData] = useState({
    email: '',
    first_name: '',
    company_name: '',
  });
  const [addingUser, setAddingUser] = useState(false);
  const [showMagicLinkModal, setShowMagicLinkModal] = useState(false);
  const [currentMagicLink, setCurrentMagicLink] = useState<string | null>(null);
  const [currentMagicLinkUser, setCurrentMagicLinkUser] = useState<string>('');
  const [generatingMagicLink, setGeneratingMagicLink] = useState<string | null>(null);
  const [exportingUser, setExportingUser] = useState<string | null>(null);

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setCurrentPage(1);
    }, 400);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery]);

  useEffect(() => {
    loadUsers();
  }, [currentPage, sortColumn, sortOrder, userTypeFilter, debouncedSearchQuery]);

  const loadUsers = async () => {
    setLoading(true);

    try {
      const result = await adminUsersApi.list({
        page: currentPage,
        per_page: perPage,
        sort_by: sortColumn,
        sort_order: sortOrder,
        filter_user_type: userTypeFilter,
        search: debouncedSearchQuery.trim() || undefined,
      });
      const usersData = result.data || [];
      const pagination = result.pagination || {};

      setTotalPages(pagination.total_pages || 1);
      setTotalUsers(pagination.total || 0);

      const normalizedUsers = usersData.map((user: any) => ({
        ...user,
        is_support_user: Array.isArray(user.support_users) ? user.support_users.length > 0 : !!user.support_users,
        support_users: undefined,
      }));

      setUsers(normalizedUsers);

      const userIds = normalizedUsers.map((u: User) => u.id);
      if (userIds.length > 0) {
        const { data: cancellations } = await supabase
          .from('subscription_cancellations')
          .select('user_id, effective_end_date')
          .is('reversed_at', null)
          .in('user_id', userIds);
        const map: Record<string, { effective_end_date: string }> = {};
        (cancellations ?? []).forEach((c: { user_id: string; effective_end_date: string }) => {
          map[c.user_id] = { effective_end_date: c.effective_end_date };
        });
        setCancellationsByUser(map);
      } else {
        setCancellationsByUser({});
      }
    } catch (error) {
      setUsers([]);
      setTotalPages(1);
      setTotalUsers(0);
      setCancellationsByUser({});
    }

    setLoading(false);
  };

  const filteredUsers = userNumberFilter.trim()
    ? users.filter(u => u.user_number !== null && String(u.user_number) === userNumberFilter.trim())
    : users;

  const handleDeleteUser = async (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      const { count: sessionsCount } = await supabase
        .from('sessions')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);
      setUserToDelete({ ...user, sessions_count: sessionsCount || 0 });
      setShowDeleteConfirm(true);
    }
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      await deleteUser(userToDelete.id);
      setShowDeleteConfirm(false);
      setUserToDelete(null);
      loadUsers();
    } catch (error: any) {
      alert('Fout bij verwijderen: ' + error.message);
    }
  };

  const handleAddUser = async () => {
    if (!newUserData.email || !newUserData.first_name || !newUserData.company_name) {
      alert('Vul alle verplichte velden in');
      return;
    }

    try {
      setAddingUser(true);

      // Generate unique widget API key via RPC
      const { data: apiKeyData, error: apiKeyError } = await supabase.rpc('generate_widget_api_key');

      if (apiKeyError || !apiKeyData) {
        throw new Error('Kon widget API key niet genereren: ' + (apiKeyError?.message || 'Onbekende fout'));
      }

      const result = await adminUpdate({
        table: 'users',
        operation: 'insert',
        data: {
          email: newUserData.email.trim(),
          first_name: newUserData.first_name.trim(),
          company_name: newUserData.company_name.trim(),
          user_profile: '',
          website_url: null,
          image_preferences: 'modern, professional',
          is_admin: false,
          widget_api_key: apiKeyData,
          widget_enabled: false,
        },
      });

      setShowAddUserModal(false);
      setNewUserData({ email: '', first_name: '', company_name: '' });

      if (result.data?.id) {
        const basePath = role === 'support' ? '/support' : '/admin';
        navigate(`${basePath}/users/${result.data.id}`);
      } else {
        loadUsers();
      }
    } catch (error: any) {
      alert('Fout bij aanmaken gebruiker: ' + error.message);
    } finally {
      setAddingUser(false);
    }
  };

  const handleGenerateMagicLink = async (userId: string, userName: string) => {
    try {
      setGeneratingMagicLink(userId);

      const result = await getActiveMagicLink(userId);

      if (!result.success || !result.magicLink) {
        alert(result.error || 'Kon magic link niet genereren');
        return;
      }

      setCurrentMagicLink(result.magicLink);
      setCurrentMagicLinkUser(userName);
      setShowMagicLinkModal(true);
    } catch (error) {
      alert('Er ging iets mis bij het genereren van de magic link');
    } finally {
      setGeneratingMagicLink(null);
    }
  };

  const handleCloseMagicLinkModal = () => {
    setShowMagicLinkModal(false);
    setCurrentMagicLink(null);
    setCurrentMagicLinkUser('');
  };

  const handleExportUser = async (userId: string, userName: string) => {
    try {
      setExportingUser(userId);
      await adminExportApi.exportUserData(userId);
    } catch (error: any) {
      alert(`Export mislukt voor ${userName}: ${error.message}`);
    } finally {
      setExportingUser(null);
    }
  };

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const getSortIcon = (column: SortColumn) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="w-3.5 h-3.5 text-gray-400" />;
    }
    return sortOrder === 'asc'
      ? <ArrowUp className="w-3.5 h-3.5 text-gray-900" />
      : <ArrowDown className="w-3.5 h-3.5 text-gray-900" />;
  };

  const formatStorageMB = (bytes: number) => {
    if (bytes === 0) return '0 MB';
    const mb = bytes / (1024 * 1024);
    return `${Math.round(mb)} MB`;
  };

  const getUserRoleIcon = (user: User) => {
    if (user.is_super_admin) {
      return (
        <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-amber-50 border border-amber-200" title="Super Admin">
          <Crown className="w-4 h-4 text-amber-700" />
        </div>
      );
    }
    if (user.is_admin) {
      return (
        <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-blue-50 border border-blue-200" title="Admin">
          <ShieldCheck className="w-4 h-4 text-blue-700" />
        </div>
      );
    }
    if (user.is_support_user) {
      return (
        <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-purple-50 border border-purple-200" title="Support User">
          <Headphones className="w-4 h-4 text-purple-700" />
        </div>
      );
    }
    return (
      <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-gray-50 border border-gray-200" title="Gebruiker">
        <UserIcon className="w-4 h-4 text-gray-400" />
      </div>
    );
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span>
            Pagina {currentPage} van {totalPages}
          </span>
          <span className="text-gray-400">•</span>
          <span>
            {totalUsers} totaal
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            className="p-2 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Eerste pagina"
          >
            <ChevronsLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="p-2 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Vorige pagina"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          {pages.map(page => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className={`min-w-[32px] px-2 py-1 rounded-lg text-sm font-medium transition-colors ${
                currentPage === page
                  ? 'bg-gray-900 text-white'
                  : 'hover:bg-gray-100 text-gray-700'
              }`}
            >
              {page}
            </button>
          ))}

          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="p-2 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Volgende pagina"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <button
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
            className="p-2 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Laatste pagina"
          >
            <ChevronsRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-semibold text-gray-900 mb-2">Gebruikers</h1>
          <p className="text-gray-600">Bekijk alle gebruikers en beheer hun gegevens</p>
        </div>

        <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
          <div className="flex-1 max-w-md relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Zoek op naam, email, bedrijf of website..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
            />
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowAddUserModal(true)}
              className="px-4 py-2.5 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-colors flex items-center gap-2"
            >
              <UserPlus className="w-4 h-4" />
              Nieuwe Gebruiker
            </button>
            <div className="relative w-24">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 font-medium select-none">#</span>
              <input
                type="number"
                min="1"
                placeholder="42"
                value={userNumberFilter}
                onChange={(e) => setUserNumberFilter(e.target.value)}
                className="w-full pl-7 pr-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
              />
            </div>
            <div className="relative">
              <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <select
                value={userTypeFilter}
                onChange={(e) => {
                  setUserTypeFilter(e.target.value as UserTypeFilter);
                  setCurrentPage(1);
                }}
                className="pl-9 pr-8 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm font-medium appearance-none bg-white cursor-pointer"
              >
                <option value="regular">Reguliere Users</option>
                <option value="admin">Admins</option>
                <option value="support">Support Users</option>
                <option value="all">Alle Users</option>
              </select>
            </div>
            <div className="text-sm text-gray-600 font-medium">
              {totalUsers} {totalUsers === 1 ? 'gebruiker' : 'gebruikers'}
            </div>
          </div>
        </div>

        {filteredUsers.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
            <UserIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">
              {debouncedSearchQuery ? 'Geen gebruikers gevonden met deze zoekcriteria' : 'Nog geen gebruikers'}
            </p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-center py-3 px-4 w-12">
                      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">#</span>
                    </th>
                    <th className="text-center py-3 px-4 w-16">
                      <div className="flex items-center justify-center" title="Role">
                        <UserIcon className="w-3.5 h-3.5 text-gray-600" />
                      </div>
                    </th>
                    <th className="text-left py-3 px-4">
                      <button
                        onClick={() => handleSort('first_name')}
                        className="flex items-center gap-1 text-xs font-semibold text-gray-600 uppercase tracking-wider hover:text-gray-900 transition-colors"
                      >
                        Gebruiker
                        {getSortIcon('first_name')}
                      </button>
                    </th>
                    <th className="text-left py-3 px-4">
                      <button
                        onClick={() => handleSort('company_name')}
                        className="flex items-center gap-1 text-xs font-semibold text-gray-600 uppercase tracking-wider hover:text-gray-900 transition-colors"
                      >
                        Bedrijf
                        {getSortIcon('company_name')}
                      </button>
                    </th>
                    <th className="text-left py-3 px-4">
                      <button
                        onClick={() => handleSort('storage_used_bytes')}
                        className="flex items-center gap-1 text-xs font-semibold text-gray-600 uppercase tracking-wider hover:text-gray-900 transition-colors"
                      >
                        <HardDrive className="w-3.5 h-3.5" />
                        Opslag
                        {getSortIcon('storage_used_bytes')}
                      </button>
                    </th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider w-12">
                      Klanttraject
                    </th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider w-24">
                      Acties
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4 text-center">
                        <span className="text-xs font-mono text-gray-400">{user.user_number ?? '—'}</span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        {getUserRoleIcon(user)}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-gray-900 text-sm">{user.first_name}</span>
                            {cancellationsByUser[user.id] && (
                              <span
                                title={`Toegang stopt ${formatDateNL(cancellationsByUser[user.id].effective_end_date)}`}
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-800 rounded"
                              >
                                <UserMinus className="w-3 h-3" />
                                Opzegging
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                            <Mail className="w-3 h-3" />
                            {user.email}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-start gap-2 text-sm text-gray-700">
                          <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="truncate max-w-[200px]">{user.company_name}</span>
                              {user.website_url && (
                                <a
                                  href={user.website_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 hover:underline"
                                >
                                  <Globe className="w-3 h-3" />
                                  Website
                                  <ExternalLink className="w-2.5 h-2.5" />
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm text-gray-700 font-medium">
                          {formatStorageMB(user.storage_used_bytes)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        {user.journey_status && user.journey_status !== 'inactive' ? (
                          <span
                            title={`Status: ${user.journey_status}`}
                            className={`inline-block w-2.5 h-2.5 rounded-full ${stoplichtBg(computeStoplicht(user.journey_status as UserJourneyStatus, user.journey_last_activity_at ?? null, false))}`}
                          />
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleGenerateMagicLink(user.id, user.first_name || user.email)}
                            disabled={generatingMagicLink === user.id}
                            className="p-1.5 hover:bg-orange-50 rounded-lg transition-colors disabled:opacity-50"
                            title="Genereer Magic Link"
                          >
                            {generatingMagicLink === user.id ? (
                              <div className="w-4 h-4 border-2 border-orange-600 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Link2 className="w-4 h-4 text-orange-600" />
                            )}
                          </button>
                          <button
                            onClick={() => {
                              const basePath = role === 'support' ? '/support' : '/admin';
                              navigate(`${basePath}/users/${user.id}`);
                            }}
                            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Bekijk Details"
                          >
                            <Eye className="w-4 h-4 text-gray-600" />
                          </button>
                          <button
                            onClick={() => handleExportUser(user.id, user.first_name || user.email)}
                            disabled={exportingUser === user.id}
                            className="p-1.5 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                            title="Exporteer gebruikersdata (ZIP)"
                          >
                            {exportingUser === user.id ? (
                              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Download className="w-4 h-4 text-blue-600" />
                            )}
                          </button>
                          {canDelete && (
                            <button
                              onClick={() => handleDeleteUser(user.id)}
                              className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                              title="Verwijderen"
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && renderPagination()}
          </div>
        )}

        {showAddUserModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-md w-full p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Nieuwe Gebruiker Toevoegen</h2>
                  <p className="text-sm text-gray-600 mt-1">Vul de basisgegevens in om een gebruiker aan te maken</p>
                </div>
                <button
                  onClick={() => {
                    setShowAddUserModal(false);
                    setNewUserData({ email: '', first_name: '', company_name: '' });
                  }}
                  className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                  disabled={addingUser}
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="email"
                    value={newUserData.email}
                    onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                    placeholder="gebruiker@bedrijf.nl"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    disabled={addingUser}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Voornaam <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    value={newUserData.first_name}
                    onChange={(e) => setNewUserData({ ...newUserData, first_name: e.target.value })}
                    placeholder="Jan"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    disabled={addingUser}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bedrijfsnaam <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    value={newUserData.company_name}
                    onChange={(e) => setNewUserData({ ...newUserData, company_name: e.target.value })}
                    placeholder="Bedrijf BV"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    disabled={addingUser}
                  />
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-900">
                    Na het aanmaken wordt je doorgestuurd naar de detail pagina waar je de overige gegevens kunt invullen.
                  </p>
                </div>
              </div>

              <div className="flex gap-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowAddUserModal(false);
                    setNewUserData({ email: '', first_name: '', company_name: '' });
                  }}
                  disabled={addingUser}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Annuleren
                </button>
                <button
                  onClick={handleAddUser}
                  disabled={addingUser || !newUserData.email || !newUserData.first_name || !newUserData.company_name}
                  className="flex-1 bg-gray-900 text-white px-4 py-2.5 rounded-lg font-medium hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <UserPlus className="w-4 h-4" />
                  {addingUser ? 'Aanmaken...' : 'Gebruiker Aanmaken'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showDeleteConfirm && userToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-md w-full p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">
                      Gebruiker Verwijderen?
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      Deze actie kan niet ongedaan gemaakt worden
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setUserToDelete(null);
                  }}
                  className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-900 font-medium mb-2">
                    Je staat op het punt om de volgende gebruiker te verwijderen:
                  </p>
                  <div className="space-y-1 text-sm text-red-800">
                    <p><strong>Naam:</strong> {userToDelete.first_name}</p>
                    <p><strong>Email:</strong> {userToDelete.email}</p>
                    <p><strong>Bedrijf:</strong> {userToDelete.company_name}</p>
                    <p><strong>Sessies:</strong> {userToDelete.sessions_count}</p>
                  </div>
                </div>

                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex gap-2 items-start">
                    <AlertTriangle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div className="space-y-2 text-sm text-yellow-900">
                      <p className="font-medium">Dit verwijdert permanent:</p>
                      <ul className="list-disc list-inside space-y-1 ml-2 text-xs">
                        <li>Alle gebruikersgegevens</li>
                        <li>Alle gekoppelde sessies ({userToDelete.sessions_count})</li>
                        <li>Alle blog ideeën en concepten</li>
                        <li>Alle gegenereerde afbeeldingen</li>
                        <li>Planningsgegevens en historie</li>
                        {userToDelete.widget_enabled && <li>Widget configuratie en analytics</li>}
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setUserToDelete(null);
                    }}
                    className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                  >
                    Annuleren
                  </button>
                  <button
                    onClick={confirmDeleteUser}
                    className="flex-1 bg-red-600 text-white px-4 py-2.5 rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Permanent Verwijderen
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <MagicLinkModal
          isOpen={showMagicLinkModal}
          magicLink={currentMagicLink}
          userName={currentMagicLinkUser}
          onClose={handleCloseMagicLinkModal}
        />
      </div>
    </AdminLayout>
  );
}
